import React from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../i18n/LocalizedLink';
import { usePageTitle } from '../hooks/usePageTitle';
import { SEOMeta } from '../templates/SEOMeta';
import { BaseLayout } from '../templates/BaseLayout';

const AboutUsEs: React.FC = () => {
  return (
    <BaseLayout>
      <SEOMeta
        title="Acerca de Nosotros - El Impostor Game"
        description="Conoce la historia detrás de El Impostor Game, un juego de deducción social creado por RurayLabs para conectar personas a través del entretenimiento."
        canonical="https://el-impostor.com/about"
      />
         <div className="min-h-screen bg-blue-50 dark:bg-gray-950 py-8 px-4">
        <div className="max-w-5xl mx-auto bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800">
          {/* Encabezado */}
          <div className="px-8 py-8 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-primary-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3 tracking-tight">🎭 Acerca de El Impostor Game</h1>
            <p className="text-lg text-gray-600 dark:text-gray-300">Conectando personas a través de la deducción social y el entretenimiento</p>
          </div>

          {/* Contenido */}
          <div className="px-8 py-8 space-y-10 text-gray-800 dark:text-gray-100 leading-relaxed">
            {/* Nuestra Historia */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                📖 Nuestra Historia
              </h2>
              <p className="text-justify text-lg">
                <strong>El Impostor Game</strong> nació de una simple idea: crear un espacio donde amigos, 
                familias y desconocidos puedan conectar a través de juegos de deducción social, sin importar 
                la distancia que los separe.
              </p>
              <p className="text-justify">
                Desarrollado por <strong>RurayLabs</strong>, nuestro equipo se apasiona por crear experiencias 
                digitales que fomentan la interacción humana genuina. Inspirados por juegos clásicos de mesa y 
                experiencias sociales modernas, diseñamos una plataforma accesible que combina lo mejor de ambos mundos.
              </p>
              <p className="text-justify">
                Desde nuestro lanzamiento, hemos conectado a miles de jugadores alrededor del mundo hispanohablante, 
                creando momentos memorables de risa, intriga y camaradería.
              </p>
            </section>

            {/* Nuestra Misión */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                🎯 Nuestra Misión
              </h2>
              <div className="bg-primary-50 border-l-4 border-primary-600 p-6 rounded-r-lg">
                <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Democratizar el entretenimiento social
                </p>
                <p className="text-justify">
                  Creemos que todos merecen acceso a experiencias de juego de calidad, sin barreras económicas 
                  ni geográficas. Por eso, El Impostor Game es completamente gratuito y accesible desde cualquier 
                  dispositivo con conexión a internet.
                </p>
              </div>
              <p className="text-justify">
                Nuestro objetivo es proporcionar una plataforma donde las personas puedan:
              </p>
              <ul className="list-disc pl-6 space-y-3 text-gray-700 dark:text-gray-200">
                <li>
                  <strong>Conectar:</strong> Reunir a amigos y familia, sin importar dónde se encuentren
                </li>
                <li>
                  <strong>Divertirse:</strong> Disfrutar de experiencias de juego emocionantes y dinámicas
                </li>
                <li>
                  <strong>Desafiar:</strong> Poner a prueba sus habilidades de deducción y persuasión
                </li>
                <li>
                  <strong>Crear recuerdos:</strong> Generar momentos memorables con sus seres queridos
                </li>
              </ul>
            </section>

            {/* Qué Ofrecemos */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                ✨ Qué Ofrecemos
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 dark:bg-blue-950/30 p-6 rounded-lg border border-blue-200 dark:border-blue-900">
                  <div className="text-3xl mb-3">🌐</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Multijugador Online</h3>
                  <p className="text-gray-700 dark:text-gray-200">
                    Conecta con amigos desde cualquier lugar del mundo. Crea salas privadas, 
                    juega en tiempo real y experimenta la emoción de descubrir al impostor.
                  </p>
                </div>

                <div className="bg-purple-50 dark:bg-purple-950/30 p-6 rounded-lg border border-purple-200 dark:border-purple-900">
                  <div className="text-3xl mb-3">🎲</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Modo Mesa</h3>
                  <p className="text-gray-700 dark:text-gray-200">
                    Perfecto para reuniones presenciales. Un solo dispositivo, interacción cara a cara 
                    y toda la diversión de un juego de mesa tradicional.
                  </p>
                </div>

                <div className="bg-green-50 dark:bg-green-950/30 p-6 rounded-lg border border-green-200 dark:border-green-900">
                  <div className="text-3xl mb-3">🆓</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Completamente Gratis</h3>
                  <p className="text-gray-700 dark:text-gray-200">
                    Sin costos ocultos, sin suscripciones, sin compras dentro de la aplicación. 
                    Solo diversión pura y accesible para todos.
                  </p>
                </div>

                <div className="bg-orange-50 dark:bg-orange-950/30 p-6 rounded-lg border border-orange-200 dark:border-orange-900">
                  <div className="text-3xl mb-3">📱</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Multi-dispositivo</h3>
                  <p className="text-gray-700 dark:text-gray-200">
                    Juega desde cualquier dispositivo: móvil, tablet o computadora. 
                    Optimizado para funcionar perfectamente en todas las plataformas.
                  </p>
                </div>
              </div>
            </section>

            {/* Nuestros Valores */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                💎 Nuestros Valores
              </h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    1
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Accesibilidad</h3>
                    <p className="text-gray-700 dark:text-gray-200">
                      Creemos que el entretenimiento de calidad debe estar al alcance de todos, 
                      independientemente de su situación económica o ubicación geográfica.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    2
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Comunidad</h3>
                    <p className="text-gray-700 dark:text-gray-200">
                      Fomentamos un ambiente inclusivo, respetuoso y acogedor donde todos los jugadores 
                      se sientan bienvenidos y seguros.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    3
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Innovación</h3>
                    <p className="text-gray-700 dark:text-gray-200">
                      Constantemente mejoramos y evolucionamos nuestra plataforma, escuchando el feedback 
                      de nuestra comunidad e implementando nuevas características.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    4
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Transparencia</h3>
                    <p className="text-gray-700 dark:text-gray-200">
                      Somos abiertos sobre cómo funcionamos, cómo usamos los datos y cómo nos financiamos 
                      a través de publicidad responsable.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* El Equipo */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                👥 El Equipo: RurayLabs
              </h2>
              <p className="text-justify">
                <strong>RurayLabs</strong> es un equipo apasionado de desarrolladores, diseñadores y 
                entusiastas de los juegos que creen en el poder del entretenimiento para unir personas.
              </p>
              <p className="text-justify">
                Nos especializamos en crear experiencias digitales que combinan tecnología moderna con 
                mecánicas de juego probadas y accesibles. Cada miembro de nuestro equipo aporta su experiencia 
                única para hacer de El Impostor Game una experiencia memorable.
              </p>
              <div className="bg-gray-50 dark:bg-gray-950/30 p-6 rounded-lg border border-gray-200 dark:border-gray-800">
                <p className="text-gray-700 dark:text-gray-200 italic">
                  "Nuestro objetivo no es solo crear un juego, sino construir una comunidad donde las personas 
                  puedan conectar, reír y crear recuerdos juntos. Cada actualización, cada característica nueva, 
                  se diseña pensando en hacer que esa conexión sea más fuerte y más accesible."
                </p>
                <p className="text-right text-gray-600 dark:text-gray-300 font-medium mt-3">— El equipo de RurayLabs</p>
              </div>
            </section>

            {/* Nuestro Compromiso */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                🤝 Nuestro Compromiso
              </h2>
              <p className="text-justify">
                Estamos comprometidos con:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-200">
                <li>Mantener la plataforma <strong>gratuita y accesible</strong> para todos</li>
                <li>Proteger la <strong>privacidad y seguridad</strong> de nuestros usuarios</li>
                <li>Escuchar y responder al <strong>feedback de la comunidad</strong></li>
                <li>Innovar constantemente para mejorar la <strong>experiencia de juego</strong></li>
                <li>Mantener un <strong>ambiente de juego positivo</strong> y libre de toxicidad</li>
                <li>Ser <strong>transparentes</strong> en nuestras prácticas y políticas</li>
              </ul>
            </section>

            {/* Tecnología */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                ⚙️ Nuestra Tecnología
              </h2>
              <p className="text-justify">
                El Impostor Game está construido con tecnologías web modernas para garantizar:
              </p>
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg text-center">
                  <div className="text-2xl mb-2">⚡</div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">Velocidad</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-200">Comunicación en tiempo real</p>
                </div>
                <div className="bg-green-50 dark:bg-green-950/30 p-4 rounded-lg text-center">
                  <div className="text-2xl mb-2">🔒</div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">Seguridad</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-200">Datos protegidos y cifrados</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-950/30 p-4 rounded-lg text-center">
                  <div className="text-2xl mb-2">📱</div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">Compatibilidad</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-200">Funciona en todos los dispositivos</p>
                </div>
              </div>
            </section>

            {/* Contacto */}
            <section className="space-y-4 bg-gradient-to-r from-primary-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 p-8 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                💬 ¿Quieres Conocer Más?
              </h2>
              <p className="text-justify text-lg">
                Nos encantaría escucharte. Ya sea que tengas preguntas, sugerencias o solo quieras 
                compartir tu experiencia con El Impostor Game, estamos aquí para ti.
              </p>
              <div className="flex flex-wrap gap-4 mt-6">
                <LocalizedLink 
                  to="/contact" 
                  className="btn-primary inline-flex items-center gap-2"
                >
                  📧 Contáctanos
                </LocalizedLink>
                <LocalizedLink 
                  to="/blog" 
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  📖 Lee Nuestro Blog
                </LocalizedLink>
                <LocalizedLink 
                  to="/" 
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  🎮 Juega Ahora
                </LocalizedLink>
              </div>
            </section>
          </div>

          {/* Footer del documento */}
          <div className="px-8 py-6 bg-gray-50 dark:bg-gray-950/30 border-t border-gray-200 dark:border-gray-800">
            <p className="text-center text-gray-600 dark:text-gray-300">
              <strong>El Impostor Game</strong> - Desarrollado con ❤️ por <strong>RurayLabs</strong>
            </p>
            <div className="flex justify-center gap-4 mt-4 flex-wrap">
              <LocalizedLink 
                to="/privacy-policy" 
                className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm"
              >
                Política de Privacidad
              </LocalizedLink>
              <LocalizedLink 
                to="/terms-of-service" 
                className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm"
              >
                Términos y Condiciones
              </LocalizedLink>
              <LocalizedLink 
                to="/contact" 
                className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm"
              >
                Contacto
              </LocalizedLink>
            </div>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

const AboutUsPt: React.FC = () => {
  return (
    <BaseLayout>
      <SEOMeta
        title="Sobre Nós - El Impostor Game"
        description="Conheça a história por trás do El Impostor Game, um jogo de dedução social criado pela RurayLabs para conectar pessoas através do entretenimento."
        canonical="https://el-impostor.com/about"
      />
         <div className="min-h-screen bg-blue-50 dark:bg-gray-950 py-8 px-4">
        <div className="max-w-5xl mx-auto bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800">
          {/* Encabezado */}
          <div className="px-8 py-8 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-primary-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3 tracking-tight">🎭 Sobre o El Impostor Game</h1>
            <p className="text-lg text-gray-600 dark:text-gray-300">Conectando pessoas por meio da dedução social e do entretenimento</p>
          </div>

          {/* Contenido */}
          <div className="px-8 py-8 space-y-10 text-gray-800 dark:text-gray-100 leading-relaxed">
            {/* Nossa História */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                📖 Nossa História
              </h2>
              <p className="text-justify text-lg">
                <strong>El Impostor Game</strong> nasceu de uma ideia simples: criar um espaço onde amigos,
                famílias e desconhecidos possam se conectar através de jogos de dedução social, não importa
                a distância que os separe.
              </p>
              <p className="text-justify">
                Desenvolvido pela <strong>RurayLabs</strong>, nosso time é apaixonado por criar experiências
                digitais que promovem a interação humana genuína. Inspirados por jogos clássicos de mesa e
                experiências sociais modernas, desenhamos uma plataforma acessível que combina o melhor dos dois mundos.
              </p>
              <p className="text-justify">
                Desde nosso lançamento, conectamos milhares de jogadores ao redor do mundo, criando momentos memoráveis
                de risadas, intriga e camaradagem.
              </p>
            </section>

            {/* Nossa Missão */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                🎯 Nossa Missão
              </h2>
              <div className="bg-primary-50 border-l-4 border-primary-600 p-6 rounded-r-lg">
                <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Democratizar o entretenimento social
                </p>
                <p className="text-justify">
                  Acreditamos que todos merecem acesso a experiências de jogo de qualidade, sem barreiras econômicas
                  ou geográficas. Por isso, El Impostor Game é completamente gratuito e acessível de qualquer dispositivo
                  com conexão à internet.
                </p>
              </div>
              <p className="text-justify">
                Nosso objetivo é oferecer uma plataforma onde as pessoas possam:
              </p>
              <ul className="list-disc pl-6 space-y-3 text-gray-700 dark:text-gray-200">
                <li>
                  <strong>Conectar:</strong> Reunir amigos e família, não importa onde estejam
                </li>
                <li>
                  <strong>Se divertir:</strong> Aproveitar experiências de jogo empolgantes e dinâmicas
                </li>
                <li>
                  <strong>Desafiar:</strong> Colocar à prova habilidades de dedução e persuasão
                </li>
                <li>
                  <strong>Criar memórias:</strong> Gerar momentos memoráveis com pessoas queridas
                </li>
              </ul>
            </section>

            {/* O que oferecemos */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                ✨ O que oferecemos
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 dark:bg-blue-950/30 p-6 rounded-lg border border-blue-200 dark:border-blue-900">
                  <div className="text-3xl mb-3">🌐</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Multijogador Online</h3>
                  <p className="text-gray-700 dark:text-gray-200">
                    Conecte-se com amigos de qualquer lugar do mundo. Crie salas privadas,
                    jogue em tempo real e viva a emoção de descobrir o impostor.
                  </p>
                </div>

                <div className="bg-purple-50 dark:bg-purple-950/30 p-6 rounded-lg border border-purple-200 dark:border-purple-900">
                  <div className="text-3xl mb-3">🎲</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Modo Mesa</h3>
                  <p className="text-gray-700 dark:text-gray-200">
                    Perfeito para encontros presenciais. Um único dispositivo, interação cara a cara
                    e toda a diversão de um jogo de mesa tradicional.
                  </p>
                </div>

                <div className="bg-green-50 dark:bg-green-950/30 p-6 rounded-lg border border-green-200 dark:border-green-900">
                  <div className="text-3xl mb-3">🆓</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Completamente grátis</h3>
                  <p className="text-gray-700 dark:text-gray-200">
                    Sem custos ocultos, sem assinaturas, sem compras dentro do aplicativo.
                    Apenas diversão pura e acessível para todos.
                  </p>
                </div>

                <div className="bg-orange-50 dark:bg-orange-950/30 p-6 rounded-lg border border-orange-200 dark:border-orange-900">
                  <div className="text-3xl mb-3">📱</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Multidispositivo</h3>
                  <p className="text-gray-700 dark:text-gray-200">
                    Jogue em qualquer dispositivo: celular, tablet ou computador.
                    Otimizado para funcionar perfeitamente em todas as plataformas.
                  </p>
                </div>
              </div>
            </section>

            {/* Valores */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">
                💎 Nossos Valores
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">🤝 Conexão humana</h3>
                  <p className="text-gray-700 dark:text-gray-200">Criar espaços onde as pessoas possam se conectar de forma autêntica.</p>
                </div>
                <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">🎯 Qualidade</h3>
                  <p className="text-gray-700 dark:text-gray-200">Oferecer experiências estáveis, seguras e divertidas.</p>
                </div>
                <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">🌍 Acessibilidade</h3>
                  <p className="text-gray-700 dark:text-gray-200">Disponibilizar jogos gratuitos e fáceis de acessar para todos.</p>
                </div>
                <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">🚀 Inovação</h3>
                  <p className="text-gray-700 dark:text-gray-200">Melhorar continuamente a experiência com ideias novas.</p>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 text-center">
              <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-200 mb-3">Pronto para jogar?</h2>
              <p className="text-gray-700 dark:text-gray-200 mb-6">
                Crie uma sala e convide seus amigos em segundos.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <LocalizedLink to="/create" className="btn-primary px-6 py-3">Criar sala</LocalizedLink>
                <LocalizedLink to="/join" className="btn-secondary px-6 py-3">Entrar na sala</LocalizedLink>
                <LocalizedLink to="/" className="btn-secondary px-6 py-3">Voltar ao início</LocalizedLink>
              </div>
            </section>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

const AboutUsEn: React.FC = () => {
  return (
    <BaseLayout>
      <SEOMeta
        title="About Us - Impostor Game"
        description="Learn the story behind Impostor Game, a social deduction game created by RurayLabs to connect people through fun."
        canonical="https://el-impostor.com/about"
      />
         <div className="min-h-screen bg-blue-50 dark:bg-gray-950 py-8 px-4">
        <div className="max-w-5xl mx-auto bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-800">
          {/* Header */}
          <div className="px-8 py-8 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-primary-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3 tracking-tight">🎭 About Impostor Game</h1>
            <p className="text-lg text-gray-600 dark:text-gray-300">Connecting people through social deduction and entertainment</p>
          </div>

          {/* Content */}
          <div className="px-8 py-8 space-y-10 text-gray-800 dark:text-gray-100 leading-relaxed">
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">📖 Our Story</h2>
              <p className="text-justify text-lg">
                <strong>Impostor Game</strong> was born from a simple idea: create a space where friends, families, and strangers
                can connect through social deduction games, no matter the distance between them.
              </p>
              <p className="text-justify">
                Built by <strong>RurayLabs</strong>, our team is passionate about creating digital experiences that foster genuine
                human interaction. Inspired by classic board games and modern social experiences, we designed an accessible
                platform that combines the best of both worlds.
              </p>
              <p className="text-justify">
                Since launch, we have connected thousands of players around the world, creating memorable moments of laughter,
                intrigue, and camaraderie.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">🎯 Our Mission</h2>
              <div className="bg-primary-50 border-l-4 border-primary-600 p-6 rounded-r-lg">
                <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Democratize social entertainment</p>
                <p className="text-justify">
                  We believe everyone deserves access to high-quality game experiences, without economic or geographic barriers.
                  That’s why Impostor Game is completely free and accessible from any internet-connected device.
                </p>
              </div>
              <p className="text-justify">Our goal is to provide a platform where people can:</p>
              <ul className="list-disc pl-6 space-y-3 text-gray-700 dark:text-gray-200">
                <li>
                  <strong>Connect:</strong> Bring friends and family together, no matter where they are
                </li>
                <li>
                  <strong>Have fun:</strong> Enjoy exciting and dynamic gameplay experiences
                </li>
                <li>
                  <strong>Challenge:</strong> Test deduction and persuasion skills
                </li>
                <li>
                  <strong>Create memories:</strong> Make unforgettable moments with the people you care about
                </li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">✨ What We Offer</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 dark:bg-blue-950/30 p-6 rounded-lg border border-blue-200 dark:border-blue-900">
                  <div className="text-3xl mb-3">🌐</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Online Multiplayer</h3>
                  <p className="text-gray-700 dark:text-gray-200">
                    Connect with friends from anywhere in the world. Create private rooms, play in real time, and feel the thrill
                    of uncovering the impostor.
                  </p>
                </div>

                <div className="bg-purple-50 dark:bg-purple-950/30 p-6 rounded-lg border border-purple-200 dark:border-purple-900">
                  <div className="text-3xl mb-3">🎲</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Table Mode</h3>
                  <p className="text-gray-700 dark:text-gray-200">
                    Perfect for in-person gatherings. One shared device, face-to-face interaction, and all the fun of a classic
                    party game.
                  </p>
                </div>

                <div className="bg-green-50 dark:bg-green-950/30 p-6 rounded-lg border border-green-200 dark:border-green-900">
                  <div className="text-3xl mb-3">🆓</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Completely Free</h3>
                  <p className="text-gray-700 dark:text-gray-200">
                    No hidden fees, no subscriptions, no in-app purchases. Just pure fun that’s accessible to everyone.
                  </p>
                </div>

                <div className="bg-orange-50 dark:bg-orange-950/30 p-6 rounded-lg border border-orange-200 dark:border-orange-900">
                  <div className="text-3xl mb-3">📱</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Multi-Device</h3>
                  <p className="text-gray-700 dark:text-gray-200">
                    Play from any device: mobile, tablet, or computer. Optimized to work smoothly across all platforms.
                  </p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">💎 Our Values</h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    1
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Accessibility</h3>
                    <p className="text-gray-700 dark:text-gray-200">
                      We believe quality entertainment should be available to everyone, regardless of economic situation or
                      location.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    2
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Community</h3>
                    <p className="text-gray-700 dark:text-gray-200">
                      We foster an inclusive, respectful, and welcoming environment where all players feel safe and included.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    3
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Innovation</h3>
                    <p className="text-gray-700 dark:text-gray-200">
                      We continuously improve and evolve the platform, listening to our community’s feedback and shipping new
                      features.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    4
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Transparency</h3>
                    <p className="text-gray-700 dark:text-gray-200">
                      We’re open about how we operate, how we use data, and how we fund the project through responsible advertising.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">👥 The Team: RurayLabs</h2>
              <p className="text-justify">
                <strong>RurayLabs</strong> is a passionate team of developers, designers, and game enthusiasts who believe in the
                power of entertainment to bring people together.
              </p>
              <p className="text-justify">
                We specialize in building digital experiences that combine modern technology with proven, accessible game
                mechanics. Each member of our team brings unique expertise to make Impostor Game a memorable experience.
              </p>
              <div className="bg-gray-50 dark:bg-gray-950/30 p-6 rounded-lg border border-gray-200 dark:border-gray-800">
                <p className="text-gray-700 dark:text-gray-200 italic">
                  "Our goal is not only to create a game, but to build a community where people can connect, laugh, and create
                  memories together. Every update and every new feature is designed to make that connection stronger and more
                  accessible."
                </p>
                <p className="text-right text-gray-600 dark:text-gray-300 font-medium mt-3">— The RurayLabs team</p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">🤝 Our Commitment</h2>
              <p className="text-justify">We are committed to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-200">
                <li>
                  Keeping the platform <strong>free and accessible</strong> for everyone
                </li>
                <li>
                  Protecting our users’ <strong>privacy and security</strong>
                </li>
                <li>
                  Listening and responding to <strong>community feedback</strong>
                </li>
                <li>
                  Constantly innovating to improve the <strong>game experience</strong>
                </li>
                <li>
                  Maintaining a <strong>positive</strong> and non-toxic game environment
                </li>
                <li>
                  Being <strong>transparent</strong> about our practices and policies
                </li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b-2 border-blue-600">⚙️ Our Technology</h2>
              <p className="text-justify">Impostor Game is built with modern web technologies to ensure:</p>
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div className="bg-blue-50 dark:bg-blue-950/30 p-4 rounded-lg text-center">
                  <div className="text-2xl mb-2">⚡</div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">Speed</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-200">Real-time communication</p>
                </div>
                <div className="bg-green-50 dark:bg-green-950/30 p-4 rounded-lg text-center">
                  <div className="text-2xl mb-2">🔒</div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">Security</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-200">Protected and encrypted data</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-950/30 p-4 rounded-lg text-center">
                  <div className="text-2xl mb-2">📱</div>
                  <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1">Compatibility</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-200">Works on all devices</p>
                </div>
              </div>
            </section>

            <section className="space-y-4 bg-gradient-to-r from-primary-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 p-8 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">💬 Want to Know More?</h2>
              <p className="text-justify text-lg">
                We’d love to hear from you. Whether you have questions, suggestions, or just want to share your experience with
                Impostor Game, we’re here for you.
              </p>
              <div className="flex flex-wrap gap-4 mt-6">
                <LocalizedLink to="/contact" className="btn-primary inline-flex items-center gap-2">
                  📧 Contact us
                </LocalizedLink>
                <LocalizedLink to="/blog" className="btn-secondary inline-flex items-center gap-2">
                  📖 Read our blog
                </LocalizedLink>
                <LocalizedLink to="/" className="btn-secondary inline-flex items-center gap-2">
                  🎮 Play now
                </LocalizedLink>
              </div>
            </section>
          </div>

          {/* Document footer */}
          <div className="px-8 py-6 bg-gray-50 dark:bg-gray-950/30 border-t border-gray-200 dark:border-gray-800">
            <p className="text-center text-gray-600 dark:text-gray-300">
              <strong>Impostor Game</strong> - Built with ❤️ by <strong>RurayLabs</strong>
            </p>
            <div className="flex justify-center gap-4 mt-4 flex-wrap">
              <LocalizedLink to="/privacy-policy" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm">
                Privacy Policy
              </LocalizedLink>
              <LocalizedLink to="/terms-of-service" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm">
                Terms and Conditions
              </LocalizedLink>
              <LocalizedLink to="/contact" className="text-blue-700 hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-200 underline text-sm">
                Contact
              </LocalizedLink>
            </div>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

const AboutUs: React.FC = () => {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const isPt = i18n.language === 'pt';

  usePageTitle(isEn ? 'About Us - Impostor Game' : isPt ? 'Sobre Nós - Impostor Game' : 'Acerca de Nosotros - El Impostor Game');

  if (isEn) return <AboutUsEn />;
  if (isPt) return <AboutUsPt />;
  return <AboutUsEs />;
};

export default AboutUs;
